import { StateBadge } from "@/components/session";
import { DataTable, type DataTableColumn } from "@/components/table";
import { Dash, ErrorNote, Hint } from "@/components/ui";
import { errorMessage } from "@/lib/api";
import { date, money, percent } from "@/lib/format";
import { useCyclePayouts } from "@/lib/queries";
import type { CyclePayout } from "@/lib/types";

/*
 * I PAYOUT DEL CICLO, SOLA LETTURA.
 *
 * Nessun pulsante, e non è una dimenticanza: un payout si registra dalla Fase
 * («Ho chiesto il payout», poi «Il payout è arrivato»), che chiude e collega il
 * periodo. Farlo avanzare da qui scriverebbe l'incasso nel registro senza
 * chiudere il periodo, e i numeri del ciclo si allontanerebbero dal metodo.
 *
 * Lordo e split sono facoltativi: dove chi ha registrato conosceva solo il
 * netto, c'è un trattino, non un numero uguagliato al netto.
 */

export function PayoutsSection({ cycleId }: { cycleId: string }) {
  const payouts = useCyclePayouts(cycleId);

  const columns: DataTableColumn<CyclePayout>[] = [
    { key: "requested", header: "Richiesto", sortBy: (p) => p.requestedAt, cell: (p) => date(p.requestedAt) },
    { key: "status", header: "Stato", sortBy: (p) => p.status, cell: (p) => <StateBadge kind="payout" state={p.status} /> },
    {
      key: "gross",
      header: "Lordo",
      align: "right",
      cell: (p) => (p.grossAmount === null ? <Dash title="Lordo non dichiarato" /> : money(p.grossAmount, p.currency)),
    },
    {
      key: "split",
      header: "Split prop",
      align: "right",
      cell: (p) => (p.splitPct === null ? <Dash title="Percentuale non dichiarata" /> : percent(p.splitPct, 0)),
    },
    { key: "net", header: "Netto", align: "right", sortBy: (p) => Number(p.netAmount), cell: (p) => money(p.netAmount, p.currency) },
    {
      key: "ledger",
      header: (
        <span className="inline-flex items-center">
          Nel registro
          <Hint>L'importo della riga di registro nata quando il payout è stato pagato, nella valuta del tenant e al tasso congelato in quel momento.</Hint>
        </span>
      ),
      align: "right",
      cell: (p) => {
        const t = p.transaction;
        if (!t || t.amountReporting === null || !t.reportingCurrency) return <Dash title="Nessuna riga di registro: il payout non è stato pagato" />;
        return (
          <span title={t.fxAssumed ? "Tasso assunto, non misurato" : undefined}>
            {money(t.amountReporting, t.reportingCurrency)}
            {t.fxAssumed && <span className="ml-1 text-xs text-warn">tasso assunto</span>}
          </span>
        );
      },
    },
    { key: "paid", header: "Pagato il", sortBy: (p) => p.paidAt ?? "", cell: (p) => (p.paidAt ? date(p.paidAt) : <Dash title="Non ancora pagato" />) },
    { key: "notes", header: "Note", cell: (p) => p.notes ?? <Dash /> },
  ];

  return (
    <section>
      <h2 className="mb-2 flex items-center text-sm font-medium text-ink">
        Payout
        <Hint>Sola lettura. Un payout si registra dalla Fase: «Ho chiesto il payout», poi «Il payout è arrivato», che lo divide e chiude il periodo.</Hint>
      </h2>
      {payouts.isLoading ? (
        <p className="text-sm text-muted">Caricamento…</p>
      ) : payouts.error ? (
        <ErrorNote>{errorMessage(payouts.error)}</ErrorNote>
      ) : (
        <div className="card">
          <DataTable
            bare
            columns={columns}
            rows={payouts.data ?? []}
            rowKey={(p) => p.id}
            initialSort={{ key: "requested", dir: "desc" }}
            empty={{
              title: "Nessun payout",
              hint: "Nasce dalla Fase, quando la prop paga: apri la fase e premi «Ho chiesto il payout», poi «Il payout è arrivato».",
            }}
          />
        </div>
      )}
    </section>
  );
}
